"use client"

import { create } from "zustand"
import { getStagedWindow, useDesktopStore } from "@/stores/desktopStore"
import { readLocalStorage, writeLocalStorage } from "@/utils/localStorage.utils"

export const SHIPMENT_CHAT_PANEL_OPEN_KEY = "wilson.shipmentChatPanel.isOpen"

export interface ShipmentPanelAsk {
    id: string
    windowId: string
    text: string
}

function readStoredIsOpen(): boolean {
    return (
        readLocalStorage({
            key: SHIPMENT_CHAT_PANEL_OPEN_KEY,
            parse: raw => raw === "true",
        }) ?? false
    )
}

interface ShipmentChatPanelState {
    panelWindowIds: ReadonlySet<string>
    isOpen: boolean
    pendingAsk: ShipmentPanelAsk | null
    askCount: number
    register: (windowId: string) => () => void
    setIsOpen: (isOpen: boolean) => void
    ask: ({ windowId, text }: { windowId: string; text: string }) => void
    clearPendingAsk: (id: string) => void
}

export const useShipmentChatPanelStore = create<ShipmentChatPanelState>()(
    (set, get) => ({
        panelWindowIds: new Set(),
        isOpen: readStoredIsOpen(),
        pendingAsk: null,
        askCount: 0,
        register: windowId => {
            set(state => ({
                panelWindowIds: new Set(state.panelWindowIds).add(windowId),
            }))
            return () =>
                set(state => {
                    const panelWindowIds = new Set(state.panelWindowIds)
                    panelWindowIds.delete(windowId)
                    return { panelWindowIds }
                })
        },
        setIsOpen: isOpen => {
            set({ isOpen })
            writeLocalStorage({
                key: SHIPMENT_CHAT_PANEL_OPEN_KEY,
                value: String(isOpen),
            })
        },
        ask: ({ windowId, text }) => {
            const askCount = get().askCount + 1
            set({
                askCount,
                pendingAsk: { id: `panel-ask-${askCount}`, windowId, text },
            })
            get().setIsOpen(true)
        },
        clearPendingAsk: id => {
            if (get().pendingAsk?.id === id) set({ pendingAsk: null })
        },
    })
)

export function askInShipmentPanel(question: string | undefined): boolean {
    const text = question?.trim()
    if (!text) return false
    const staged = getStagedWindow(useDesktopStore.getState().windows)
    const panel = useShipmentChatPanelStore.getState()
    if (!staged || !panel.panelWindowIds.has(staged.id)) return false
    panel.ask({ windowId: staged.id, text })
    return true
}
